/**
 * Frame-spike recorder — attribution for FPS spikes.
 *
 * PerfHud tells you *when* a frame is slow (worst-frame + session peak). It
 * cannot tell you *what code ran* during that slow frame. This module fills that
 * gap using the browser's Long Animation Frames API (LoAF): a PerformanceObserver
 * that fires on any animation frame that took ≥ THRESHOLD_MS, handing us the exact
 * scripts that ran during it — sourceURL, function name, script duration, and the
 * time spent in forced style/layout. That is spike-to-code attribution: the
 * "hotspots causing the spikes."
 *
 * Where LoAF is unavailable (older Chromium, and iOS WKWebView / Safari — which
 * support NEITHER LoAF nor longtask) we fall back to the older Long Tasks API,
 * which still gives a duration but no per-script breakdown. If neither exists the
 * recorder is a no-op and reports itself unsupported (mirrors how the HUD's `mem`
 * row degrades on iOS — deep frame profiling there is Safari Timelines' job).
 *
 * Cost model matches perfCounters/latency-probe: the observer only runs while the
 * HUD is open (startSpikeRecorder on mount, stopSpikeRecorder on cleanup), so
 * production pays nothing. Even while running, LoAF/longtask callbacks fire ONLY
 * on frames that already blew the budget — rare by definition — so there is no
 * per-frame overhead.
 */

// LoAF (and longtask) only surface frames/tasks at or above 50ms; the spec floor
// for LoAF's durationThreshold is also 50ms. Keeping our threshold here documents
// intent and lets us raise it later without hunting for the magic number.
const THRESHOLD_MS = 50;

// Cap the recent-spikes ring buffer. Enough to eyeball a burst without letting a
// long session grow the array unbounded.
const MAX_RECENT = 40;

/** One script that ran during a spiking frame (LoAF only; empty on longtask). */
export type SpikeScript = {
  /** Script source URL, trimmed to the filename for readability. 'inline' if none. */
  source: string;
  /** Function / handler name the browser attributed the work to. */
  functionName: string;
  /** Wall-clock ms this script ran during the frame. */
  durationMs: number;
  /** ms of that time spent in forced style/layout (layout thrash tell). */
  forcedLayoutMs: number;
};

/** One recorded spike (a long animation frame, or a long task on the fallback). */
export type Spike = {
  at: number; // Date.now() when it landed
  kind: 'loaf' | 'longtask';
  /** Total frame (LoAF) or task (longtask) duration in ms. */
  durationMs: number;
  /** Main-thread blocking time in ms (LoAF blockingDuration; == duration on longtask). */
  blockingMs: number;
  /** Per-script breakdown, worst-first. Empty for longtask. */
  scripts: SpikeScript[];
};

/** Aggregated offender across the whole session, keyed by function@source. */
export type SpikeOffender = {
  key: string;
  /** Number of spiking frames this script appeared in. */
  hits: number;
  /** Total ms attributed to it across all spikes. */
  totalMs: number;
  /** Total forced style/layout ms — a high share here means layout thrash. */
  forcedLayoutMs: number;
};

export type SpikeSummary = {
  /** True if LoAF or longtask is available and the observer is attached. */
  supported: boolean;
  /** 'loaf' (rich) | 'longtask' (duration only) | 'none'. */
  mode: 'loaf' | 'longtask' | 'none';
  /** Total spikes seen since the recorder started (or last reset). */
  total: number;
  /** Worst single blocking duration seen (ms). */
  worstMs: number;
  /** Session offenders, worst (highest totalMs) first. Empty in longtask mode. */
  offenders: SpikeOffender[];
  /** Most recent spikes, newest last. */
  recent: Spike[];
};

let observer: PerformanceObserver | null = null;
let mode: 'loaf' | 'longtask' | 'none' = 'none';
let total = 0;
let worstMs = 0;
const offenders = new Map<string, SpikeOffender>();
const recent: Spike[] = [];

/** Which entry type this browser can give us, preferring the richer LoAF. */
const detectMode = (): 'loaf' | 'longtask' | 'none' => {
  try {
    const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: string[] })
      .supportedEntryTypes;
    if (!supported) return 'none';
    if (supported.includes('long-animation-frame')) return 'loaf';
    if (supported.includes('longtask')) return 'longtask';
    return 'none';
  } catch {
    return 'none';
  }
};

/** 'https://host/static/js/main.abc.js' -> 'main.abc.js'. Empty URL -> 'inline'. */
const shortSource = (url: string): string => {
  if (!url) return 'inline';
  try {
    const path = new URL(url, window.location.href).pathname;
    return path.slice(path.lastIndexOf('/') + 1) || url;
  } catch {
    return url.slice(url.lastIndexOf('/') + 1) || url;
  }
};

// LoAF's PerformanceScriptTiming isn't in the ambient TS lib yet; describe just
// the fields we read so we stay type-safe without pulling extra @types.
type ScriptTiming = {
  sourceURL?: string;
  sourceFunctionName?: string;
  invoker?: string;
  duration?: number;
  forcedStyleAndLayoutDuration?: number;
};
type LoafEntry = PerformanceEntry & {
  blockingDuration?: number;
  scripts?: ScriptTiming[];
};

/** Fold a spike's scripts into the session offender aggregate. */
const recordOffenders = (scripts: SpikeScript[]): void => {
  for (const s of scripts) {
    const key = `${s.functionName || '(anonymous)'} @ ${s.source}`;
    const prev = offenders.get(key);
    if (prev) {
      prev.hits += 1;
      prev.totalMs += s.durationMs;
      prev.forcedLayoutMs += s.forcedLayoutMs;
    } else {
      offenders.set(key, {
        key,
        hits: 1,
        totalMs: s.durationMs,
        forcedLayoutMs: s.forcedLayoutMs
      });
    }
  }
};

const handleEntries = (list: PerformanceObserverEntryList): void => {
  for (const entry of list.getEntries()) {
    if (entry.duration < THRESHOLD_MS) continue;

    let spike: Spike;
    if (mode === 'loaf') {
      const loaf = entry as LoafEntry;
      const scripts: SpikeScript[] = (loaf.scripts ?? [])
        .map((sc) => ({
          source: shortSource(sc.sourceURL ?? ''),
          functionName: sc.sourceFunctionName || sc.invoker || '(anonymous)',
          durationMs: sc.duration ?? 0,
          forcedLayoutMs: sc.forcedStyleAndLayoutDuration ?? 0
        }))
        .sort((a, b) => b.durationMs - a.durationMs);
      spike = {
        at: Date.now(),
        kind: 'loaf',
        durationMs: entry.duration,
        blockingMs: loaf.blockingDuration ?? entry.duration,
        scripts
      };
      recordOffenders(scripts);
    } else {
      spike = {
        at: Date.now(),
        kind: 'longtask',
        durationMs: entry.duration,
        blockingMs: entry.duration,
        scripts: []
      };
    }

    total += 1;
    if (spike.blockingMs > worstMs) worstMs = spike.blockingMs;
    recent.push(spike);
    if (recent.length > MAX_RECENT) recent.shift();
  }
};

/** Whether this browser can attribute spikes at all. */
export const isSpikeRecorderSupported = (): boolean => detectMode() !== 'none';

/**
 * Start observing long frames. Idempotent — a second call while already running
 * is a no-op. Safe to call unconditionally; on unsupported browsers it just
 * flips mode to 'none' and records nothing.
 */
export const startSpikeRecorder = (): void => {
  if (observer) return;
  mode = detectMode();
  if (mode === 'none') return;
  try {
    observer = new PerformanceObserver(handleEntries);
    if (mode === 'loaf') {
      // durationThreshold is LoAF-only; longtask ignores options.
      observer.observe({ type: 'long-animation-frame', durationThreshold: THRESHOLD_MS } as any);
    } else {
      observer.observe({ type: 'longtask', buffered: true } as any);
    }
  } catch {
    // Observe can throw if the type is unsupported despite the capability check.
    observer = null;
    mode = 'none';
  }
};

/** Stop observing. Session totals/offenders are kept so a dump still works. */
export const stopSpikeRecorder = (): void => {
  if (!observer) return;
  try {
    observer.disconnect();
  } catch {
    // ignore
  }
  observer = null;
};

/** Wipe session totals, offenders, and the recent buffer. Wire to the HUD's ↺. */
export const resetSpikes = (): void => {
  total = 0;
  worstMs = 0;
  offenders.clear();
  recent.length = 0;
};

/**
 * Print the current spike summary to the console: a headline line plus a
 * worst-first offender table (which functions ran during slow frames). Same
 * data the HUD tap-dump shows, callable directly — e.g. `window.dumpSpikes()`
 * (exposed by PerfHud while it's open) or from a test harness. Returns the
 * summary too, so callers can assert on it.
 */
export const logSpikeSummary = (): SpikeSummary => {
  const s = getSpikeSummary();
  /* eslint-disable no-console */
  if (!s.supported) {
    console.log('Spikes: not supported on this browser (no LoAF/longtask — e.g. iOS).');
    return s;
  }
  console.log(
    `Spikes: ${s.total} long frames (${s.mode}), worst ${s.worstMs.toFixed(0)}ms blocking`
  );
  if (s.offenders.length > 0) {
    console.table(
      s.offenders.slice(0, 15).map((o) => ({
        'function @ source': o.key,
        hits: o.hits,
        'total ms': Math.round(o.totalMs),
        'forced layout ms': Math.round(o.forcedLayoutMs)
      }))
    );
  } else if (s.mode === 'longtask') {
    console.log('(longtask fallback: no per-script breakdown — use Chrome DevTools LoAF)');
  }
  /* eslint-enable no-console */
  return s;
};

/**
 * The spike summary as shareable plain text.
 *
 * `logSpikeSummary` writes to the console, which is unreachable on a device
 * running the installed app — no address bar, no devtools, and on iOS not even
 * a remote inspector without a Mac. The attribution this recorder exists to
 * produce was therefore stranded exactly where it is most needed: on the
 * player's own hardware, where the jank actually reproduces.
 *
 * Same shape as `formatBenchmarkReport`, so a pasted spike report and a pasted
 * benchmark report read alike in a ticket.
 */
export const formatSpikeSummary = (summary: SpikeSummary = getSpikeSummary()): string => {
  if (!summary.supported) {
    return [
      '=== PERF SPIKES ===',
      'unsupported on this browser (no LoAF or longtask — e.g. iOS WKWebView).',
      'Capture on Android Chromium for per-script attribution.'
    ].join('\n');
  }
  const lines = [
    '=== PERF SPIKES ===',
    `when:  ${new Date().toISOString()}`,
    `mode:  ${summary.mode}`,
    `total: ${summary.total} long frames`,
    `worst: ${summary.worstMs.toFixed(0)}ms blocking`,
    ''
  ];
  if (summary.offenders.length === 0) {
    lines.push(
      summary.mode === 'longtask'
        ? 'OFFENDERS: none — longtask fallback carries no per-script breakdown.'
        : 'OFFENDERS: none recorded.'
    );
    return lines.join('\n');
  }
  lines.push('OFFENDERS (worst first)');
  summary.offenders.slice(0, 15).forEach((o) => {
    lines.push(
      `  ${Math.round(o.totalMs)}ms over ${o.hits} hit(s)` +
        `${o.forcedLayoutMs ? `, ${Math.round(o.forcedLayoutMs)}ms forced layout` : ''}` +
        ` — ${o.key}`
    );
  });
  return lines.join('\n');
};

/**
 * Hand the spike report to the OS share sheet, falling back to the clipboard
 * and finally the console. Mirrors `shareBenchmark` exactly — the share sheet is
 * the only export route that works inside a Capacitor WebView on both platforms.
 */
export const shareSpikeSummary = async (): Promise<void> => {
  const text = formatSpikeSummary();
  try {
    const nav = navigator as any;
    if (typeof nav.share === 'function') {
      await nav.share({ title: 'Perf spikes', text });
      return;
    }
  } catch {
    // fall through to clipboard
  }
  try {
    await navigator.clipboard.writeText(text);
    // eslint-disable-next-line no-console
    console.log('Spike report copied to clipboard.');
  } catch {
    // eslint-disable-next-line no-console
    console.log(text);
  }
};

/** Read the current session summary without mutating it. */
export const getSpikeSummary = (): SpikeSummary => ({
  supported: mode !== 'none',
  mode,
  total,
  worstMs,
  offenders: Array.from(offenders.values()).sort((a, b) => b.totalMs - a.totalMs),
  recent: recent.slice()
});
